import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, ProgressBar } from 'react-bootstrap';

const ProgressModal = ({ show, progress, message, onCancel }) => {
  // Make sure progress is a valid number
  const normalizedProgress = Number.isFinite(progress) ? progress : 0;
  
  // Handle backdrop clicks using the Modal's own props
  const isComplete = normalizedProgress >= 100;
  
  // Auto-close the modal after completion
  useEffect(() => {
    let timeoutId;
    
    if (isComplete && show) {
      // Auto-close after completion with a delay
      timeoutId = setTimeout(() => {
        onCancel(); // Use the cancel function to close the modal
      }, 1500); // 1.5 seconds
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isComplete, show, onCancel]);
  
  // Determine progress bar variant based on progress
  const getProgressVariant = () => {
    if (normalizedProgress < 30) return 'info';
    if (normalizedProgress < 70) return 'primary';
    if (normalizedProgress < 100) return 'warning';
    return 'success';
  };

return (
  <Modal
    show={show}
    backdrop="static"
    keyboard={isComplete}
    centered
    size="sm"
    aria-labelledby="progressModalLabel"
    onHide={() => {
      if (isComplete) {
        onCancel();
      }
    }}
    className="progress-modal-phenix"
  >
    <Modal.Header
      className={`progress-modal-phenix-header border-0 ${
        isComplete ? 'bg-success' : 'bg-primary'
      } text-white`}
    >
      <Modal.Title
        id="progressModalLabel"
        className="progress-modal-phenix-title fs-6 w-100 text-center"
      >
        {isComplete ? '✅ Operation Complete' : '⏳ Operation in Progress'}
      </Modal.Title>
    </Modal.Header>

    <Modal.Body className="progress-modal-phenix-body px-4 py-3 text-center">
      <div className="progress-modal-phenix-bar-wrapper mb-3">
        <ProgressBar
          now={normalizedProgress}
          variant={getProgressVariant()}
          animated={normalizedProgress < 100}
          label={`${Math.round(normalizedProgress)}%`}
          className="progress-modal-phenix-bar rounded-pill"
        />
      </div>

      <p className="progress-modal-phenix-message small text-black mb-0">
        {message ||
          (isComplete
            ? 'Your task was completed successfully.'
            : 'Please wait while we process your request...')}
      </p>
    </Modal.Body>

    <Modal.Footer className="progress-modal-phenix-footer border-0 d-flex justify-content-center pt-0 pb-3">
      {normalizedProgress < 100 ? (
        <Button
          variant="outline-danger"
          size="sm"
          onClick={onCancel}
          className="progress-modal-phenix-cancel-btn"
        >
          Cancel
        </Button>
      ) : (
        <Button
          variant="success"
          size="sm"
          onClick={onCancel}
          className="progress-modal-phenix-close-btn"
        >
          Close
        </Button>
      )}
    </Modal.Footer>
  </Modal>
);


};

ProgressModal.propTypes = {
  show: PropTypes.bool.isRequired,
  progress: PropTypes.number,
  message: PropTypes.string,
  onCancel: PropTypes.func
};

ProgressModal.defaultProps = {
  progress: 0,
  message: '',
  onCancel: () => {}
};

export default ProgressModal;